import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatInputModule, MatLabel } from '@angular/material/input';
import { AuthService } from '../../../../core/services/auth.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { User } from '../../models/user.model';
import { LoginRequest } from '../../models/login-request.model';
import { SnackBarService } from '../../../services/snackbar.service';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RecaptchaService } from '../../../../core/services/recaptcha.service';
import { catchError, EMPTY, finalize, map, mergeMap, tap } from 'rxjs';
import { GtmService } from '../../../../core/services/gtm.service';
import { HapticsService } from '../../../general/services/haptics.service';
import { LoaderComponent } from '../../../general/components/loader/loader.component';
import { SeoService } from '../../../services/seo.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatLabel,
    MatError,
    ReactiveFormsModule,
    MatFormField,
    MatInputModule,
    MatCardModule,
    MatButtonModule,
    RouterLink,
    MatProgressSpinnerModule,
    MatHint,
    LoaderComponent
  ],
  templateUrl: './login.html',
  styleUrl: './login.scss',
  standalone: true
})
export class LoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private recaptchaService = inject(RecaptchaService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(SnackBarService);
  private gtm = inject(GtmService);
  private cdr = inject(ChangeDetectorRef);
  private haptics = inject(HapticsService);
  private platformId = inject(PLATFORM_ID);
  private seo = inject(SeoService);

  @Input() isDialogMode = false;
  @Output() loginComplete = new EventEmitter<User>();
  @Output() goToSignup = new EventEmitter<void>();
  
  loginForm: FormGroup;
  isInvalidCredentials = false;
  returnUrl = '/profile';
  loading = false;
  hide = true;

  handleSignupRedirect() {
    if (this.isDialogMode) {
      this.goToSignup.emit();
    } else {
      this.router.navigate(['/signup']);
    }
  }

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    if(isPlatformBrowser(this.platformId)) {
      document.body.scrollTop = 0;
      this.gtm.pageView({
        page_path: '/login',
        page_title: 'Login | Horizinns'
      });
    }
    
    this.seo.updateMetaTags('Login', 'User Login for Horizinns', undefined, `${environment.appUrl}/login`);

    this.seo.addJsonLd({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Login",
      "description": "User Login for Horizinns",
      "url": "${environment.appUrl}/login"
    });

    this.route.queryParamMap.subscribe(params => {
      this.returnUrl = params.get('returnUrl') || '/';
    });

    if (this.authService.isAuthenticatedSnapshot && isPlatformBrowser(this.platformId)) {
      console.log('Already authenticated → handling redirect or dialog close');
      
      if (this.isDialogMode) {
        setTimeout(() => {
          this.loginComplete.emit(); 
        });
      } else {
        // Standard standalone behavior: route them away
        this.router.navigateByUrl(this.returnUrl);
      }
    }
  }

  submit() {
    this.haptics.click();
    if (this.loginForm.invalid) return;
    
    this.loading = true;

    this.recaptchaService.execute('login').pipe(
      mergeMap(token => {
        const request: LoginRequest = {
          username: this.loginForm.get('email')?.value || '',
          password: this.loginForm.get('password')?.value || '',
          recaptchaToken: token
        };
        return this.authService.login(request);
      }),
      
      tap(response => console.log('Raw login response:', response?.email)),

      map(user => {
        if (!user) {
          this.haptics.error();
          throw new Error('Invalid credentials');
        }
        this.haptics.success();
        return user;
      }),

      catchError((err) => {
        this.haptics.error();
        console.log('Login failed:', err);
        this.isInvalidCredentials = true;
        this.loading = false;
        this.cdr.detectChanges();
        return EMPTY;
      }),

      // Only successful logins reach here
      tap({
        next: (user: User) => {
          if (isPlatformBrowser(this.platformId)) {
            this.trackLogin(user);
            this.isInvalidCredentials = false;

            if (this.isDialogMode) {
              this.loginComplete.emit(user);
            } else {
              this.router.navigateByUrl(this.returnUrl);
            }
          }
        }
      }),

      finalize(() => this.loading = false)
    ).subscribe();
  }

  trackLogin(user: User) {
    this.gtm.pushEvent('user_login', {
      user_id: user.id,
      username: user.username
    });
  }
}
